import React, {useContext} from 'react';
import * as R from 'ramda';
import {RevenueCtx} from '@/contexts';
import {Table, Card, CardBody, Row, Col, Badge} from 'reactstrap';

import {mapWithIndex, blank, numberFormat, maybe} from '@/util';
import Icon from '@/views/shared/Icon';
import styles from '@/b2b/index.module.css';
import AmoebaLink from '@/components/AmoebaLink';

const payoutList = mapWithIndex((payout, idx) => (
  <tr key={`revenue_payout_${idx}`} className={payout.trColor}>
    <td>
      <Badge
        color={payout.numColor}
        style={{
          paddingRight: '9px',
          paddingLeft: '9px',
          borderRadius: '9px',
        }}
      >
        {payout.num}
      </Badge>
    </td>
    <td>
      {maybe(payout.showArrowIcon).map(() => (
        <Icon icon='arrow-right' style={{margin: '0 10px 0 20px'}} />
      ))}
      <AmoebaLink href={`/producers/${payout.producerId}`}>
        {payout.producerName}
      </AmoebaLink>
    </td>
    <td className={styles.align_right}>
      <div className={styles.payout_item}>
        <div className={styles.payout_item_detail}>
          <Badge color={payout.badgeColor}>
            {numberFormat.toPercentage(payout.totalRate, 4)}
          </Badge>
        </div>

        {maybe(payout.totalRate > 0).map(() => (
          <div
            key={`settlement_item_${idx}`}
            className={styles.payout_settlement_detail}
          >
            <div className={styles.payout_settlement_detail_heading}>
              <div className='badge badge-info'>Rate Details</div>
            </div>
            <div className={styles.payout_settlement_item_detail}>
              <div className='badge badge-secondary'>Initial</div>
              {R.prop('rateInfo', payout.payoutRatePopup)}
              <div className='badge badge-secondary'>Override</div>
              {R.prop('overrideRateInfo', payout.payoutRatePopup)}
              <div className='badge badge-secondary'>Effective Date</div>
              {R.prop('payoutEffectiveDate', payout.payoutRatePopup)}
            </div>
          </div>
        ))}
      </div>
    </td>
    <td className='text-right'>
      {numberFormat.toDollar(payout.totalPayAmount)}
    </td>
    <td className='text-right'>
      {numberFormat.toDollar(payout.settledAmount)}
    </td>
    <td className='text-right'>
      {numberFormat.toDollar(payout.outstandingAmount)}
    </td>
  </tr>
));

const Summary = () => {
  const data = useContext(RevenueCtx);

  if (blank(data)) {
    return null;
  }

  return (
    <>
      <b>Remarks</b>
      <Card>
        <CardBody className=''>
          <Row>
            <Col md={6}>
              <strong>Description</strong>
            </Col>
            <Col md={6}>
              <strong>Internal Remark</strong>
            </Col>
          </Row>
          <Row>
            <Col md={6}>{R.defaultTo('-', data.summary.description)}</Col>
            <Col md={6}>{R.defaultTo('-', data.summary.internalRemark)}</Col>
          </Row>
        </CardBody>
      </Card>
      <b>PayIn Summary</b>
      <Card>
        <CardBody className=''>
          <Row>
            <Col md={2}>
              <strong>Total Installment No.</strong>
            </Col>
            <Col md={2}>
              <strong>Total Rate</strong>
            </Col>
            <Col md={2}>
              <strong>No. of settled</strong>
            </Col>
            <Col md={2} className='text-right'>
              <strong>Total Entitled Payin Amount</strong>
            </Col>
            <Col md={2} className='text-right'>
              <strong>Total Settled Payin Amount</strong>
            </Col>
            <Col md={2} className='text-right'>
              <strong>Total Outstanding Payin Amount</strong>
            </Col>
          </Row>
          <Row>
            <Col md={2}>
              {R.defaultTo('-', data.summary.totalInstallmentNo)}
            </Col>
            <Col md={2}>
              {maybe(data.summary.payinTotalRate).map(() =>
                numberFormat.toPercentage(data.summary.payinTotalRate, 4)
              )}
              &nbsp;
              {maybe(data.summary.showBadge).map(() => (
                <span key='showBadge' className='badge badge-secondary'>
                  {' '}
                  <div className={styles.align_left}>
                    <div className={styles.payin_item}>
                      <div className={styles.payin_item_detail}>
                        <Icon icon='info' />
                      </div>
                      <div className={styles.payin_settlement_detail}>
                        <div className={styles.payin_settlement_detail_heading}>
                          <Badge color='secondary'>Rate Details</Badge>
                        </div>
                        <div className={styles.payin_settlement_item_detail}>
                          <span>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Rate</Badge>
                              </Col>
                              <Col md={9}>{data.payin.rate.rate}</Col>
                            </Row>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Annual Bonus</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.annualBonus}</Col>
                              <Col md={3}>
                                <Badge color='secondary'>Single Topup</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.singleTopup}</Col>
                            </Row>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Pay Term</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.payTerm}</Col>
                              <Col md={3}>
                                <Badge color='secondary'>Renewal</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.renewal}</Col>
                            </Row>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Issue Age</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.issueAge}</Col>
                              <Col md={3}>
                                <Badge color='secondary'>Trail</Badge>
                              </Col>
                              <Col md={3}>{data.payin.rate.trail}</Col>
                            </Row>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Sum Insured</Badge>
                              </Col>
                              <Col md={9}>{data.payin.rate.sumInsured}</Col>
                            </Row>
                            <Row>
                              <Col md={3}>
                                <Badge color='secondary'>Effective Date</Badge>
                              </Col>
                              <Col md={9}>{data.payin.rate.effectiveDate}</Col>
                            </Row>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </span>
              ))}
            </Col>
            <Col md={2}>{R.defaultTo('-', data.summary.noOfSettled)}</Col>
            <Col md={2} className='text-right'>
              {numberFormat.toDollar(data.summary.totalEntitledPayinAmount)}
            </Col>
            <Col md={2} className='text-right'>
              {numberFormat.toDollar(data.summary.totalSettledPayinAmount)}
            </Col>
            <Col md={2} className='text-right'>
              {numberFormat.toDollar(data.summary.totalOutstandingPayinAmount)}
            </Col>
          </Row>
        </CardBody>
      </Card>
      <b>PayOut Summary</b>
      <Card>
        <CardBody>
          <Table>
            <thead>
              <tr>
                <th />
                <th>Payee</th>
                <th className='text-right'>Rate</th>
                <th className='text-right'>Entitled Amount</th>
                <th className='text-right'>Settled Amount</th>
                <th className='text-right'>Outstanding Amount</th>
              </tr>
            </thead>
            <tbody>
              {payoutList(data.summary.payouts)}
              <tr className='table-success'>
                <th scope='row' />
                <th scope='row' className='text-right'>
                  Total
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toPercentage(data.summary.payoutTotalRate, 4)}
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toDollar(data.summary.totalPayAmount)}
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toDollar(data.summary.totalSettledAmount)}
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toDollar(data.summary.totalOutstandingAmount)}
                </th>
              </tr>
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default Summary;
